// Supabase Client - Frontend only
// This uses the PUBLIC anon key (safe to expose in browser)

import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if we're in demo mode (missing credentials)
export const isDemoMode = !supabaseUrl || !supabaseAnonKey ||
    supabaseUrl === 'https://your-project.supabase.co' ||
    supabaseAnonKey === 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...';

// Create real client or null for demo mode
let supabaseClient: SupabaseClient | null = null;

if (!isDemoMode) {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey);
}

// Export the client (may be null in demo mode)
export const supabase = supabaseClient as SupabaseClient;

// Helper to get the current session token for API calls
export async function getAuthToken(): Promise<string | null> {
    if (isDemoMode) {
        return 'demo-token-for-preview';
    }
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
}

// Demo user for preview mode
const DEMO_USER = {
    id: 'demo-user-id',
    email: 'demo@scpagency.com',
    confirmed_at: new Date().toISOString(),
};

// Auth helpers with demo mode support
export const auth = {
    // Sign up with email/password
    async signUp(email: string, password: string) {
        if (isDemoMode) {
            return { user: DEMO_USER, session: null };
        }
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
        });
        if (error) throw error;
        return data;
    },

    // Sign in with email/password
    async signIn(email: string, password: string) {
        if (isDemoMode) {
            // In demo mode, any login works
            return {
                user: { ...DEMO_USER, email },
                session: { access_token: 'demo-token' }
            };
        }
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        if (error) throw error;
        return data;
    },

    // Sign out
    async signOut() {
        if (isDemoMode) {
            return;
        }
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    },

    // Get current user
    async getUser() {
        if (isDemoMode) {
            return null; // Not logged in by default in demo
        }
        const { data: { user } } = await supabase.auth.getUser();
        return user;
    },

    // Listen for auth state changes
    onAuthStateChange(callback: (event: string, session: any) => void) {
        if (isDemoMode) {
            // Return a mock subscription that does nothing
            return { data: { subscription: { unsubscribe: () => { } } } };
        }
        return supabase.auth.onAuthStateChange(callback);
    }
};
